import {
  Router,
  NavigationEnd,
  RouterLinkActive,
  RouterLink,
} from '@angular/router';
import { NgClass } from '@angular/common';
import { Component, ElementRef, OnInit, Renderer2, HostListener, OnDestroy, DOCUMENT, inject } from '@angular/core';
import { RouteInfo } from './sidebar.metadata';
import { AuthService, CenterBrandingService } from '@core';
import { TranslateModule } from '@ngx-translate/core';
import { NgScrollbar } from 'ngx-scrollbar';
import { UnsubscribeOnDestroyAdapter } from '@shared';
import { SidebarService } from './sidebar.service';
import { AuthorizationRequirement } from '@core/models/interface';
import { AuthorizationService } from '@core/service/authorization.service';
import { LocalStorageService } from '@shared/services/storage.service';
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [
    NgScrollbar,
    RouterLinkActive,
    RouterLink,
    NgClass,
    TranslateModule,
  ],
})
export class SidebarComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit, OnDestroy {
  private document = inject<Document>(DOCUMENT);
  private renderer = inject(Renderer2);
  elementRef = inject(ElementRef);
  private authService = inject(AuthService);
  private authorizationService = inject(AuthorizationService);
  private router = inject(Router);
  private sidebarService = inject(SidebarService);
  private centerBrandingService = inject(CenterBrandingService);
  private localStorageService = inject(LocalStorageService);
  public sidebarItems!: RouteInfo[];
  public innerHeight?: number;
  public bodyTag!: HTMLElement;
  listMaxHeight?: string;
  listMaxWidth?: string;
  userFullName?: string;
  userImg?: string;
  userInitials = 'US';
  userType?: string;
  companyLogo = 'assets/images/logo.png';
  companyName = 'Tdsystem';
  headerHeight = 60;
  currentRoute?: string;
  private expandedItems = new Set<string>();
  constructor() {
    super();
    this.elementRef.nativeElement.closest('body');
    this.subs.sink = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.closeMobileSidebar();
      }
    });
  }
  @HostListener('window:resize', ['$event'])
  windowResizecall(_event: Event) {
    // Using _event parameter to satisfy linting
    this.setMenuHeight();
    this.checkStatuForResize(false);
  }
  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const clickedSidebarToggle = !!target?.closest(
      '.mobile-header-action--left, .bars, .navbar-toggle, .sidemenu-collapse'
    );

    if (clickedSidebarToggle) {
      return;
    }

    if (!this.elementRef.nativeElement.contains(target)) {
      this.closeMobileSidebar();
    }
  }

  callToggleMenu(event: Event, item: RouteInfo, length: number) {
    if (length > 0) {
      event.preventDefault();
      const menuItem = this.getMenuId(item);

      if (this.expandedItems.has(menuItem)) {
        this.expandedItems.delete(menuItem);
        return;
      }

      this.expandedItems.add(menuItem);
    }
  }
  ngOnInit() {
    const branding = this.centerBrandingService.getBranding();
    this.companyLogo = branding.companyLogo;
    this.companyName = branding.companyName;
    this.subs.sink = this.centerBrandingService.branding$.subscribe((nextBranding) => {
      this.companyLogo = nextBranding.companyLogo;
      this.companyName = nextBranding.companyName;
    });

    if (this.authService.currentUserValue) {
      this.syncCurrentUser();
      this.subs.sink = this.authService.change().subscribe(() => this.syncCurrentUser());
      this.subs.sink = this.sidebarService.getRouteInfo().subscribe((routes: RouteInfo[]) => {
        this.sidebarItems = this.filterAuthorizedRoutes(routes);
        this.expandActiveParents(this.sidebarItems);
      });

      // console.log(userRole)
      // if (userRole === Role.Admin) {
      //   this.userType = Role.Admin;
      // } else if (userRole === Role.Patient) {
      //   this.userType = Role.Patient;
      // } else if (userRole === Role.Doctor) {
      //   this.userType = Role.Doctor;
      // } else {
      //   this.userType = Role.Admin;
      // }
    }

    // this.sidebarItems = ROUTES.filter((sidebarItem) => sidebarItem);
    this.initLeftSidebar();
    this.bodyTag = this.document.body;
  }
  initLeftSidebar() {
    const _this = this;
    // Set menu height
    _this.setMenuHeight();
    _this.checkStatuForResize(true);
  }
  setMenuHeight() {
    this.innerHeight = window.innerHeight;
    const height = this.innerHeight - this.headerHeight;
    this.listMaxHeight = height + '';
    this.listMaxWidth = '500px';
  }
  isOpen() {
    return this.bodyTag.classList.contains('overlay-open');
  }

  checkStatuForResize(_firstTime: boolean) {
    if (window.innerWidth < 1025) {
      this.renderer.addClass(this.document.body, 'ls-closed');
    } else {
      this.renderer.removeClass(this.document.body, 'ls-closed');
    }
  }
  mouseHover() {
    const body = this.elementRef.nativeElement.closest('body');
    if (body.classList.contains('submenu-closed')) {
      this.renderer.addClass(this.document.body, 'side-closed-hover');
      this.renderer.removeClass(this.document.body, 'submenu-closed');
    }
  }
  mouseOut() {
    const body = this.elementRef.nativeElement.closest('body');
    if (body.classList.contains('side-closed-hover')) {
      this.renderer.removeClass(this.document.body, 'side-closed-hover');
      this.renderer.addClass(this.document.body, 'submenu-closed');
    }
  }
  logout() {
    this.subs.sink = this.authService.logout().subscribe((res) => {
      if (res.success) {
        this.router.navigate(['/authentication/signin']);
      }
    });
  }

  private filterAuthorizedRoutes(items: RouteInfo[]): RouteInfo[] {
    return items
      .map((item) => {
        const submenu = item.submenu?.length
          ? this.filterAuthorizedRoutes(item.submenu)
          : [];

        return {
          ...item,
          submenu,
        };
      })
      .filter((item) => this.canDisplayRoute(item));
  }

  private canDisplayRoute(item: RouteInfo): boolean {
    const requirement = item.auth || this.getLegacyRoleRequirement(item.role);
    const hasVisibleChildren = !!item.submenu?.length;
    const hasOwnPath = !!item.path;
    const hasOwnRequirement = !!requirement;

    if (item.groupTitle) {
      return true;
    }

    if (hasVisibleChildren) {
      return !hasOwnRequirement || this.authorizationService.hasAccess(requirement);
    }

    if (!hasOwnPath) {
      return hasOwnRequirement && this.authorizationService.hasAccess(requirement);
    }

    return this.authorizationService.hasAccess(requirement);
  }

  private getLegacyRoleRequirement(roles?: string[]): AuthorizationRequirement | undefined {
    return this.authorizationService.fromLegacyRoles(roles);
  }

  getMenuId(item: RouteInfo): string {
    return item.path || item.title;
  }

  isExpanded(item: RouteInfo): boolean {
    return this.expandedItems.has(this.getMenuId(item));
  }

  private expandActiveParents(items: RouteInfo[]) {
    items.forEach((item) => {
      if (this.hasActiveDescendant(item)) {
        this.expandedItems.add(this.getMenuId(item));
      }

      if (item.submenu?.length) {
        this.expandActiveParents(item.submenu);
      }
    });
  }

  private hasActiveDescendant(item: RouteInfo): boolean {
    if (!item.submenu?.length) {
      return !!item.path && this.router.isActive(item.path, {
        paths: 'exact',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      });
    }

    return item.submenu.some((child) => this.hasActiveDescendant(child));
  }

  private syncCurrentUser() {
    this.userFullName = this.authService.currentUserValue.name;
    this.userImg = this.authService.currentUserValue.avatar || '';
    this.userInitials = this.buildInitials(
      this.userFullName || this.authService.currentUserValue.email || ''
    );
  }

  private buildInitials(value: string): string {
    return value
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('') || 'US';
  }

  private closeMobileSidebar(): void {
    this.renderer.removeClass(this.document.body, 'overlay-open');

    if (window.innerWidth < 1025) {
      this.renderer.addClass(this.document.body, 'ls-closed');
      this.renderer.addClass(this.document.body, 'side-closed');
      this.renderer.addClass(this.document.body, 'submenu-closed');
      this.renderer.removeClass(this.document.body, 'side-closed-hover');
      this.localStorageService.set('collapsed_menu', 'true');
    }
  }
}
